import React, { useEffect, useState, useRef, useContext } from 'react'
import { useLocation } from "react-router-dom";
import { ExcelExport } from '@progress/kendo-react-excel-export';
// * Helpers
import Notifications, { ERROR_NOTIFICATION, SUCCESS_NOTIFICATION } from '../../helpers/notifications';
import { useTranslation } from 'react-i18next';
// Import axios
import { AMS } from "../../services/api";
// Context
import AuthContext from '../../context/Auth/AuthContext';
// Spreadsheet control
import { Spreadsheet } from "@progress/kendo-spreadsheet-react-wrapper";
// Spinner
import { Loader } from "@progress/kendo-react-indicators";

// Matrix actions (buttons and title) bar
import MatrixActionsBar from './matrixActionsBar';

const MatrixSpreadSheet = (props) => {
    const { t } = useTranslation('common');
    //* To can acess the parameters sent via history.push
    const location = useLocation();
    //handle rows and columns sizes
    const [spreadDim, setSpreadDim] = useState({ rows: 1, columns: 1 });
    //data for the spread
    const [dataSource, setDataSource] = useState({});
    //ref to the spread
    const spread = useRef(null);
    const _export = useRef(null);
    // if we need change the dimension of rows or columns of the spread, we need no show the spread, so that it reload
    const [showSpread, setShowSpread] = useState(false);
    // Session context to get the current institution data
    const { user } = useContext(AuthContext);

    // * matrix meta data
    const [matrixMetadata, setMatrixMetadata] = useState(null)
    // * matrix data
    const [matrixData, setMatrixData] = useState(null)

    useEffect(() => {
        //When tree change this useeffect update the matrix
        if (props.selectedNodeData) {
            // * API CALLS
            // Gets matrix metadata
            if (props.selectedNodeData.type === 'matrix') {
                getMatrixMetadaData(props.selectedNodeData.code);
                getMatrixData(props.selectedNodeData.code, props.selectedNodeData.initialSelectors);
            } else {
                setMatrixMetadata(null)
                getReportData(props.selectedNodeData.code);
            }
            // Gets matrix data
        }
    }, [props.selectedNodeData]);// eslint-disable-line react-hooks/exhaustive-deps



    // Calls the API to get the matrix data
    const getMatrixData = async (matrixcode, selectors) => {
        try {
            setShowSpread(false);
            const response = await AMS.get(`/spreadsheet/SCENARIO/${location.state.detail.scenarioId}/${matrixcode}/${selectors}`, {
                params: {
                    usr: user.email
                }
            });
            if (response.data.result === -1) {
                Notifications.notify(response.data.message, ERROR_NOTIFICATION);
                setMatrixData(null);
                props.setBlockTree(false);

                return;
            }
            setMatrixData(response.data);

            const data = response.data.spreadsheet;

            //update rows and columns for the spread
            setSpreadDim({
                rows: data.rows,
                columns: data.columns
            });
            //set dataSource
            setDataSource(data.sheets[0]);
            //we show the spread
            setShowSpread(true);
            fillSpreadSheet(data.sheets[0]);
            validate_edit(response.data.datablockranges, response.data.ischeckedout ? user.email === response.data.checkoutuser : false);
        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error);
            props.setBlockTree(false);

        }
    }

    // Calls the API to get the report data
    const getReportData = async (matrixcode) => {
        try {
            setShowSpread(false);
            const response = await AMS.get(`/report/SCENARIO/${location.state.detail.scenarioId}/${matrixcode}`);
            if (response.data.result === -1) {
                Notifications.notify(response.data.message, ERROR_NOTIFICATION);
                setMatrixData(null);
                props.setBlockTree(false);
                return;
            }
            setMatrixData(response.data);

            const data = JSON.parse(response.data.data);

            //update rows and columns for the spread
            setSpreadDim({
                rows: data.rows,
                columns: data.columns
            });
            //set dataSource
            setDataSource(data.sheets[0]);
            //we show the spread
            setShowSpread(true);
            fillSpreadSheet(data.sheets[0]);
        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error);
            props.setBlockTree(false);

        }
    }


    // Calls the API to get the matrix metadata
    const getMatrixMetadaData = async (matrixcode) => {
        try {
            const response = await AMS.get(`/metadata/SCENARIO/${location.state.detail.scenarioId}/${matrixcode}`);
            if (response.data.result === -1) {
                Notifications.notify(response.data.message, ERROR_NOTIFICATION);
                setMatrixMetadata(null);
                props.setBlockTree(false);

                return;
            }
            setMatrixMetadata(response.data);

        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION)
            console.log(error);
            props.setBlockTree(false);

        }
    }

    // Fills the spreadsheet with the data
    const fillSpreadSheet = async (data) => {
        // First: Builds the spreadsheet with the template information
        const sheet = spread.current ? spread.current.widgetInstance.activeSheet() : null;
        // to avoid kendo errors
        if (sheet === null) {
            return;
        }

        sheet.fromJSON(data);

        props.setBlockTree(false);
    }

    const validate_edit = (blocks, ischeckout) => {
        // If the wizard is in VIEW mode, the Spreadsheet cannot be edited
        const sheet = spread.current ? spread.current.widgetInstance.activeSheet() : null;
        if (sheet === null) {
            return;
        }
        blocks.forEach(block => {
            sheet.range(block).enable(ischeckout);
        });

    }




    // * In the matrixData variable, the values entered for Females and Males are concatenated as a string.
    // * Separated by ; to assemble the data when it is to be displayed
    const saveMatrixData = async (selectors) => {
        try {
            // spreadsheet instance
            const sheet = spread.current.widgetInstance.activeSheet();
            if (sheet === null) {
                return;
            }
            const data = matrixData.datablockranges.map(block => {
                const blockData = sheet.range(block).values();
                return blockData.flat();
            });
            const dataFlat = data.flat();
            if (!checkSPData(data.flat())) {
                Notifications.notify(t(`model.spWrongData`), ERROR_NOTIFICATION);
                return;
            }
            const saveRes = await AMS.post(`/save/SCENARIO/${location.state.detail.scenarioId}/${props.selectedNodeData.code}/${selectors}`, {
                usr: user.email,
                data: dataFlat
            });

            if (saveRes.data.result === -1) {
                Notifications.notify(saveRes.data.message, ERROR_NOTIFICATION);
                return;
            }

            Notifications.notify(t(`matrixcontrol.actions.success_save`), SUCCESS_NOTIFICATION);

        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error)
        }
    }

    // * Checkin matrix
    const checkinMatrixData = async (selectors) => {
        try {
            await saveMatrixData(selectors);
            const checkinRes = await AMS.get(`/checkin/SCENARIO/${location.state.detail.scenarioId}/${props.selectedNodeData.code}/${selectors}`, {
                params: {
                    usr: user.email
                }
            });
            if (checkinRes.data.result === -1) {
                Notifications.notify(checkinRes.data.message, ERROR_NOTIFICATION);
                return;
            }
            // * get checkout data to update matrixData
            const checkoutInfoRes = await AMS.get(`/getcheckoutinfo/SCENARIO/${location.state.detail.scenarioId}/${props.selectedNodeData.code}/${selectors}`);

            if (checkoutInfoRes.data.result === -1) {
                Notifications.notify(checkoutInfoRes.data.message, ERROR_NOTIFICATION);
                return;
            }

            setMatrixData({ ...matrixData, ...checkoutInfoRes.data });
            // * enable spread
            validate_edit(matrixData.datablockranges, false);
        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error)
        }

    }

    // !Checks if the array has string values
    // !If it has string values, the function returns FALSE that means the SP its wrong
    const checkSPData = (arr) => {
        let result = arr.every(function (e) {
            return typeof e === 'number';
        });
        return result;
    }

    const cleanMatrixData = () => {
        try {
            // spreadsheet instance
            const sheet = spread.current.widgetInstance.activeSheet();
            if (sheet === null) {
                return;
            }
            matrixData.datablockranges.forEach(block => {
                sheet.range(block).value(0);
            });

        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error)
        }
    }

    const exportData = () => {
        try {
            const spreadInstance = spread.current.widgetInstance;
            if (spreadInstance === null) {
                return;
            }
            spreadInstance.saveAsExcel();
        } catch (error) {
            Notifications.notify(String(error), ERROR_NOTIFICATION);
            console.log(error)
        }
    }

    return (
        <>
            <div className="row matrix_title_row">
                <MatrixActionsBar
                    selectedNodeData={props.selectedNodeData} matrixMetadata={matrixMetadata}
                    matrixData={matrixData} getMatrixData={getMatrixData}
                    setMatrixData={setMatrixData} validate_edit={validate_edit}
                    saveMatrixData={saveMatrixData} checkinMatrixData={checkinMatrixData}
                    exportData={exportData} cleanMatrixData={cleanMatrixData}
                />
            </div>
            <div className="row  row_attrs container_steps">

                <div className="col-md-12" >
                    {showSpread ? (
                        <ExcelExport data={dataSource} ref={_export}>
                            <Spreadsheet
                                columns={spreadDim.columns}
                                rows={spreadDim.rows}
                                ref={spread}
                                className="test"
                                excelExport={(e) => {
                                    e.workbook.fileName = `${props.selectedNodeData.code}.xlsx`;
                                }}
                            ></Spreadsheet>
                        </ExcelExport>
                    ) : <Loader size="large" type="converging-spinner" />}
                </div>
            </div>
        </>

    );
}

export default MatrixSpreadSheet